import {
  Action,
  Reducer,
} from 'redux';

import ActionTypes from '../../ActionTypes';
import { censorProfanity } from '../../helpers/stringUtils';
import {
  ConversationList,
  ConversationsListError,
  ConversationsListState,
  PlaceholderState,
} from '../../reduxTypes';
import { MessageActions } from '../../types';

interface LoadConversationsListErrorActionPayload extends Action {
    error: ConversationsListError
}

interface ConversationsListSuccessActionPayload extends Action {
    conversationsList: ConversationList
}

interface ConversationsListPlaceholderActionPayload extends Action {
    placeholder: PlaceholderState
}

interface ConversationsListResetUnreadActionPayload extends Action {
    conversationId: string
}

interface ConversationsListSetActivePayload extends Action {
    activeConversationId: string
}

const defaultPlaceholderState  = {
    placeholderRoute: '',
    placeholderInfo: {},
    active: false
};

export const defaultState: ConversationsListState = {
    conversationsList: [],
    loading: true,
    unread: 0,
    activeConversation: '',
    placeholder: (defaultPlaceholderState as PlaceholderState),
};

// Clean profanity in each conversation's last_message so that 'crap' becomes '****'
export const cleanLastMessageProfanity = conversation => {
    if (conversation.last_message.action === MessageActions.SEND) {
        return conversation;
    }

    return {
        ...conversation,
        last_message: {
            ...conversation.last_message,
            content: censorProfanity(conversation.last_message.content)
        }
    };
};

const ConversastionsListReducer: Reducer<ConversationsListState> = (state: ConversationsListState = defaultState, action: Action): ConversationsListState => {
    switch (action.type) {
        case ActionTypes.CONVERSATIONSLIST_LOAD_MESSAGE:
            return {
                ...state,
                loading: true
            };

        case ActionTypes.CONVERSATIONSLIST_LOAD_ERROR:
            return {
                ...state,
                loading: false,
                error: (action as LoadConversationsListErrorActionPayload).error
            };

        case ActionTypes.CONVERSATIONSLIST_SET_ACTIVE:
            const { activeConversationId } = (action as ConversationsListSetActivePayload);
            return {
                ...state,
                activeConversation: activeConversationId
            };

        case ActionTypes.CONVERSATIONSLIST_RESET_UNREAD:
            const { conversationId } = (action as ConversationsListResetUnreadActionPayload);
            return {
                ...state,
                conversationsList: (state as ConversationsListState).conversationsList.map((conversation) => {
                    if (conversation.id === conversationId) {
                        conversation.unread_message_count = '0';
                        return conversation;
                    }
                    return conversation;
                })
            };

        case ActionTypes.CONVERSATIONSLIST_GET_PLCHLDR:
            const { placeholder } = (action as ConversationsListPlaceholderActionPayload);
            return {
                ...state,
                placeholder
            };

        case ActionTypes.CONVERSATIONSLIST_OFF_PLCHLDR:
            const inactivePlaceholder = {...state.placeholder, placeholderRoute: ''};
            return {
                ...state, placeholder: inactivePlaceholder
            };

        case ActionTypes.CONVERSATIONSLIST_LOAD_SUCCESS:
            const dirtyConversationsList = (action as ConversationsListSuccessActionPayload).conversationsList;
            const cleanConversationsList = dirtyConversationsList.map(cleanLastMessageProfanity);
            const unreadConversations = cleanConversationsList.map(conversation => Number(conversation.unread_message_count) !== 0 && state.activeConversation !== conversation.id ? true : null);
            return {
                ...state,
                loading: false,
                conversationsList: cleanConversationsList,
                unread: unreadConversations.filter(conversation => !!conversation).length,
            };

        case ActionTypes.CONVERSATIONSLIST_MAP_COUNTER:
            const unreadConvos = ((state as ConversationsListState).conversationsList).filter(conversation => Number(conversation.unread_message_count) !== 0 && conversationId !== conversation.id ? true : null);
            const unreadNumber = unreadConvos.length;
            return {
                ...state,
                unread: unreadNumber
            };

        default:
            return state;
    }
};

export default ConversastionsListReducer;



// WEBPACK FOOTER //
// ./src/sections/ConversationsList/ConversationsListReducer.ts